/**
 * 全景播放器：热点跳转 + 悬浮缩略图切场景（Pannellum CDN）
 * 注意：勿把会变的函数放进 effect 依赖，否则父组件轮询会反复 Loading。
 */
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { message } from '../utils/antdMessage';
import {
  authPanoramaImageUrl,
  destroyPannellumViewer,
  loadPannellum
} from '../utils/pannellumLoader';
import './OrthoToolbox.css';

function destroyViewer(viewerRef) {
  destroyPannellumViewer(viewerRef);
}

function authImageUrl(path) {
  return authPanoramaImageUrl(path);
}

export default function PanoramaViewer({
  open,
  projectId,
  initialSceneId,
  onClose,
  loadBundle
}) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const loadBundleRef = useRef(loadBundle);
  const mountedSceneRef = useRef(null); // 当前已挂到 pannellum 的 sceneId
  const [bundle, setBundle] = useState(null);
  const [sceneId, setSceneId] = useState(null);
  const [bootLoading, setBootLoading] = useState(false); // 仅首次拉项目
  const [sceneSwitching, setSceneSwitching] = useState(false); // 切场景轻提示，不拆画布

  loadBundleRef.current = loadBundle;

  // 打开/换项目时拉一次数据（不依赖 loadBundle 引用）
  useEffect(() => {
    if (!open || !projectId) {
      destroyViewer(viewerRef);
      mountedSceneRef.current = null;
      setBundle(null);
      setSceneId(null);
      setBootLoading(false);
      setSceneSwitching(false);
      return undefined;
    }
    let cancelled = false;
    setBootLoading(true);
    (async () => {
      try {
        const data = await loadBundleRef.current(projectId);
        if (cancelled) return;
        setBundle(data);
        const scenes = (data.scenes || []).filter((s) => s.ossKey && s.published !== false);
        const cover = data.project?.coverSceneId;
        const start = initialSceneId
          || cover
          || scenes[0]?.id
          || null;
        setSceneId(start);
      } catch (e) {
        if (!cancelled) message.error(e.message || '加载全景失败');
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅 open/projectId；initial 只在打开时用一次
  }, [open, projectId]);

  // 场景变化时创建/重建播放器（同 scene 不重复建）
  useEffect(() => {
    if (!open || !bundle || !sceneId) return undefined;

    const scene = (bundle.scenes || []).find((s) => Number(s.id) === Number(sceneId));
    if (!scene?.imageUrl) return undefined;

    if (Number(mountedSceneRef.current) === Number(sceneId) && viewerRef.current) {
      return undefined;
    }

    let cancelled = false;
    setSceneSwitching(true);

    const hotspots = (bundle.hotspots || [])
      .filter((h) => Number(h.fromSceneId) === Number(sceneId))
      .map((h) => ({
        id: `hs-${h.id}`,
        pitch: h.pitch,
        yaw: h.yaw,
        text: h.title || '前往',
        targetSceneId: Number(h.toSceneId),
        cssClass: 'zhxt-pano-hotspot'
      }));

    (async () => {
      try {
        // 等 Modal 内容挂到 DOM
        await new Promise((r) => requestAnimationFrame(() => r()));
        if (cancelled || !containerRef.current) return;

        const pannellum = await loadPannellum();
        if (cancelled || !containerRef.current) return;

        destroyViewer(viewerRef);
        containerRef.current.innerHTML = '';

        viewerRef.current = pannellum.viewer(containerRef.current, {
          type: 'equirectangular',
          panorama: authImageUrl(scene.imageUrl),
          autoLoad: true,
          showControls: true,
          compass: true,
          yaw: scene.initialYaw || 0,
          pitch: scene.initialPitch || 0,
          hfov: 100,
          hotSpots: hotspots.map((hs) => ({
            id: hs.id,
            pitch: hs.pitch,
            yaw: hs.yaw,
            type: 'info',
            text: hs.text,
            cssClass: hs.cssClass,
            clickHandlerFunc: () => {
              setSceneId(hs.targetSceneId);
            }
          }))
        });
        mountedSceneRef.current = Number(sceneId);
      } catch (e) {
        if (!cancelled) message.error(e.message || '全景播放器加载失败');
      } finally {
        if (!cancelled) setSceneSwitching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, bundle, sceneId]);

  // 关闭时销毁
  useEffect(() => {
    if (open) return undefined;
    destroyViewer(viewerRef);
    mountedSceneRef.current = null;
    return undefined;
  }, [open]);

  const scenes = (bundle?.scenes || []).filter((s) => s.ossKey);
  const sceneHotspotCount = (bundle?.hotspots || [])
    .filter((h) => Number(h.fromSceneId) === Number(sceneId))
    .length;

  return (
    <Modal
      open={open}
      title={bundle?.project?.title || '全景影像'}
      onCancel={onClose}
      footer={null}
      width="92vw"
      style={{ top: 24 }}
      destroyOnClose
      closable={false}
      styles={{ body: { padding: 0, height: '78vh', display: 'flex', flexDirection: 'column' } }}
    >
      <div className="zhxt-pano-stage">
        {/* 画面右上角浅色关闭 */}
        <button
          type="button"
          className="zhxt-pano-close"
          onClick={onClose}
          aria-label="关闭全景"
          title="关闭"
        >
          ×
        </button>
        {bootLoading && (
          <div className="zhxt-pano-boot">正在打开项目…</div>
        )}
        {!bootLoading && sceneSwitching && (
          <div className="zhxt-pano-switch-tip">切换场景…</div>
        )}
        {!bootLoading && !sceneSwitching && sceneHotspotCount > 0 && (
          <div className="zhxt-pano-hotspot-hint">
            本场景有 {sceneHotspotCount} 个热点，请左右拖动环视查找蓝点标签
          </div>
        )}
        <div
          ref={containerRef}
          className="zhxt-pano-canvas"
          style={{ visibility: bootLoading ? 'hidden' : 'visible' }}
        />
        {/* 缩略图悬浮在全景画面底部，不占用独立高度 */}
        {scenes.length > 0 && (
          <div className="zhxt-pano-thumbs">
            {scenes.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`zhxt-pano-thumb ${Number(s.id) === Number(sceneId) ? 'active' : ''}`}
                onClick={() => {
                  if (Number(s.id) === Number(sceneId)) return;
                  setSceneId(s.id);
                }}
                title={s.name}
              >
                <img src={authImageUrl(s.thumbUrl || s.imageUrl)} alt={s.name} />
                <span>{s.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
